import React from "react";
import { toast } from "sonner";
import { X, Copy, Envelope } from "@phosphor-icons/react";

/**
 * Iter-73.5 — inline preview of the vendor integration kit + one-click
 * copy for pasting into a personal email client.
 *
 * Shows every field the `POST /send-integration-kit` email contains,
 * with a Copy button on each row PLUS a big "Copy full email body"
 * button that puts a ready-to-paste plaintext version on the clipboard.
 *
 * Also renders a mailto: shortcut that opens the admin's default mail
 * client pre-filled with the affiliate's contact_email as the To,
 * a sensible subject, and the full plaintext body — so the fastest
 * path is one click.
 */
export default function IntegrationKitPreview({ kit, onClose }) {
  if (!kit) return null;
  const copy = (label, val) => {
    navigator.clipboard.writeText(val || "");
    toast.success(`${label} copied`);
  };

  const subject = `Your LeadFlow integration kit — ${kit.affiliate_name || ""}`;
  const mailto = kit.contact_email
    ? `mailto:${encodeURIComponent(kit.contact_email)}`
      + `?subject=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(kit.email_plaintext || "")}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center overflow-y-auto p-4"
      data-testid="integration-kit-preview-modal"
    >
      <div className="bg-white border-2 border-black shadow-[8px_8px_0_#000] w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b-2 border-black p-4">
          <div>
            <div className="font-chivo font-black uppercase tracking-tight text-lg">
              Integration kit — {kit.affiliate_name}
            </div>
            <div className="label-mono">
              copy any field · paste into your personal email
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="kit-preview-close"
            className="border border-black p-2 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Full email — the big copy button */}
          <div className="border-2 border-black bg-emerald-50 p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="font-chivo font-black uppercase tracking-tight text-sm">
                One-click copy
              </div>
              <div className="label-mono ml-auto">plaintext, ready to paste</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => copy("Full email body", kit.email_plaintext)}
                data-testid="kit-copy-full-plaintext"
                className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold inline-flex items-center gap-2"
              >
                <Copy size={12} weight="bold" />
                Copy full email body
              </button>
              {mailto && (
                <a
                  href={mailto}
                  data-testid="kit-open-mailto"
                  className="border-2 border-black bg-white px-4 py-2 uppercase text-[11px] tracking-widest font-bold inline-flex items-center gap-2 hover:bg-black hover:text-white"
                  title={`Opens your default mail client, pre-fills ${kit.contact_email}`}
                >
                  <Envelope size={12} weight="bold" />
                  Open in mail app
                </a>
              )}
              <button
                type="button"
                onClick={() => copy("HTML email body", kit.email_html)}
                data-testid="kit-copy-html"
                className="border border-black bg-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white"
              >
                Copy HTML instead
              </button>
            </div>
            <div className="text-[10.5px] text-zinc-600 mt-2 leading-relaxed">
              <b>Copy full email body</b> gives you plaintext perfect for Gmail /
              Outlook. <b>Open in mail app</b> opens your default mail client
              pre-filled with{" "}
              <span className="font-mono">{kit.contact_email || "the affiliate"}</span>{" "}
              already in To. <b>Copy HTML</b> preserves formatting if your
              client accepts rich HTML paste.
            </div>
          </div>

          {/* Structured fields */}
          <FieldRow
            label="Live API key"
            value={kit.live_api_key}
            testId="kit-live-key"
            tint="bg-emerald-100"
            hint="Real leads — dispatches to the buyer"
          />
          <FieldRow
            label="Sandbox API key"
            value={kit.sandbox_api_key}
            testId="kit-sandbox-key"
            tint="bg-blue-100"
            hint="Test leads — is_test=true, never delivers"
          />
          <FieldRow
            label="Webhook URL"
            value={kit.webhook_url}
            testId="kit-webhook-url"
            tint="bg-zinc-100"
          />
          <FieldRow
            label="Affiliate dashboard"
            value={kit.vendor_dashboard_url}
            testId="kit-dashboard-url"
            tint="bg-zinc-100"
          />

          <div className="border border-black p-3 bg-white" data-testid="kit-landing-urls">
            <div className="font-chivo font-black uppercase tracking-tight text-xs mb-2">
              Tracking URLs ({kit.landing_urls?.length || 0})
            </div>
            {kit.landing_urls_warning && (
              <div
                className="mb-3 border-2 border-amber-600 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900"
                data-testid="kit-landing-urls-warning"
              >
                <b>⚠ Not yet approved:</b> {kit.landing_urls_warning}
              </div>
            )}
            <div className="text-[10.5px] text-zinc-600 mb-3 leading-relaxed">
              Pre-filled with <span className="font-mono">?aff={kit.public_source_id}</span>.
              Copy any one and paste it directly into an ad or email. Rows
              marked <span className="inline-block px-1.5 py-0.5 bg-emerald-600 text-white uppercase tracking-widest font-bold text-[9px]">Custom buyer</span>
              route <b>directly</b> to that specific buyer's destination.
            </div>
            <div className="space-y-1.5">
              {(kit.landing_urls || []).map((row) => (
                <div key={row.slug} className="flex items-center gap-2 flex-wrap">
                  <div className="text-[11px] font-bold min-w-[220px] inline-flex items-center gap-1.5">
                    <span>{row.title}</span>
                    {row.is_custom && (
                      <span
                        className="px-1.5 py-0.5 bg-emerald-600 text-white uppercase tracking-widest font-bold text-[9px]"
                        data-testid={`kit-landing-custom-tag-${row.slug}`}
                      >
                        Custom buyer
                      </span>
                    )}
                  </div>
                  <input
                    readOnly
                    value={row.url}
                    className="flex-1 border border-black px-2 py-1 font-mono text-[10.5px] bg-zinc-50 min-w-[280px]"
                    data-testid={`kit-landing-input-${row.slug}`}
                  />
                  <button
                    type="button"
                    onClick={() => copy(row.title, row.url)}
                    data-testid={`kit-landing-copy-${row.slug}`}
                    className="border border-black p-1.5 hover:bg-black hover:text-white"
                    title="Copy"
                  >
                    <Copy size={11} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <details className="border border-black p-3 bg-zinc-50">
            <summary className="cursor-pointer font-chivo font-black uppercase tracking-tight text-xs">
              Full plaintext preview
            </summary>
            <pre className="mt-3 text-[10.5px] whitespace-pre-wrap font-mono bg-white p-3 border border-zinc-300 max-h-[400px] overflow-auto" data-testid="kit-plaintext-preview">
              {kit.email_plaintext}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value, testId, tint, hint }) {
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    toast.success(`${label} copied`);
  };
  return (
    <div className="border border-black p-3 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${tint}`}>
          {label}
        </div>
        {hint && (
          <div className="text-[10px] text-zinc-500 tracking-wide">{hint}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value || ""}
          className="flex-1 border border-black px-3 py-2 font-mono text-[11px] bg-zinc-50"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={copy}
          data-testid={`${testId}-copy`}
          className="border border-black p-2 hover:bg-black hover:text-white"
          title="Copy"
        >
          <Copy size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
